import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockBetween,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  PreviewCard,
  Button,
  Icon,
  DataTable,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
  RSelect,
} from "../../../components/Component";
import { Badge, Row, Col, Spinner } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const riskBadge = {
  high: { color: "danger", label: "Eleve" },
  medium: { color: "warning", label: "Moyen" },
  low: { color: "success", label: "Faible" },
};

const ComplianceIntelligentAssistantPage = () => {
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [payload, setPayload] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  const [filters, setFilters] = useState({
    site_id: "",
    days_unresolved: 30,
    risk_level: "all",
  });

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    loadData(1);
  }, [filters.site_id, filters.days_unresolved, filters.risk_level]);

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setSites(rows);
    } catch (_) {
      setSites([]);
    }
  };

  const loadData = async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        days_unresolved: filters.days_unresolved,
        risk_level: filters.risk_level,
      };
      if (filters.site_id) {
        params.site_id = filters.site_id;
      }

      const response = await api.getComplianceIntelligentAssistant(params);
      setPayload(response || null);
      setCurrentPage(response?.pagination?.current_page || page);
    } catch (error) {
      toast.error(error?.message || "Erreur lors du chargement de l assistant conformite");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  const siteOptions = useMemo(() => {
    const base = [{ value: "", label: "Tous les sites" }];
    const rows = sites.map((site) => ({
      value: String(site.id),
      label: site.code ? `${site.name} (${site.code})` : site.name,
    }));
    return [...base, ...rows];
  }, [sites]);

  const rows = payload?.data || [];
  const summary = payload?.summary || {};
  const auditSummary = payload?.audit_ready_summary || {};
  const pagination = payload?.pagination || {};
  const totalItems = pagination.total || 0;

  const fetchAllRowsForExport = async () => {
    const all = [];
    let page = 1;
    let lastPage = 1;
    do {
      const params = {
        page,
        per_page: 100,
        days_unresolved: filters.days_unresolved,
        risk_level: filters.risk_level,
      };
      if (filters.site_id) {
        params.site_id = filters.site_id;
      }
      const response = await api.getComplianceIntelligentAssistant(params);
      const chunk = Array.isArray(response?.data) ? response.data : [];
      all.push(...chunk);
      lastPage = response?.pagination?.last_page || 1;
      page += 1;
    } while (page <= lastPage);
    return all;
  };

  const exportExcel = async () => {
    if (exportExcelLoading || exportPdfLoading) return;
    try {
      setExportExcelLoading(true);
      const allRows = await fetchAllRowsForExport();
      if (!allRows.length) {
        toast.info("Aucune ligne a exporter");
        return;
      }

      const { downloadComplianceAssistantExcelStyled } = await import("../../../utils/exportComplianceAssistantExcelStyled");
      await downloadComplianceAssistantExcelStyled({
        rows: allRows,
        summary,
        filters,
        generatedAt: new Date(),
      });
      toast.success("Export Excel telecharge");
    } catch (error) {
      toast.error(error?.message || "Erreur lors de l export Excel");
    } finally {
      setExportExcelLoading(false);
    }
  };

  const exportPdf = async () => {
    if (exportExcelLoading || exportPdfLoading) return;
    try {
      setExportPdfLoading(true);
      const allRows = await fetchAllRowsForExport();
      if (!allRows.length) {
        toast.info("Aucune ligne a exporter");
        return;
      }

      const { downloadComplianceAssistantPdf } = await import("../../../utils/downloadComplianceAssistantPdf");
      await downloadComplianceAssistantPdf({
        rows: allRows,
        summary,
        auditSummary,
        filters,
        generatedAt: new Date(),
      });
      toast.success("Export PDF telecharge");
    } catch (error) {
      toast.error(error?.message || "Erreur lors de l export PDF");
    } finally {
      setExportPdfLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Head title="Assistant conformite intelligent" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Assistant conformite intelligent</BlockTitle>
              <BlockDes className="text-soft">
                Detection des ecarts critiques non traites et resume pret audit
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="light" className="me-2" disabled={exportExcelLoading || exportPdfLoading} onClick={exportExcel}>
                {exportExcelLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    <span>Export Excel...</span>
                  </>
                ) : (
                  <>
                    <Icon name="download-cloud" />
                    <span>Export Excel</span>
                  </>
                )}
              </Button>
              <Button color="light" className="me-2" disabled={exportExcelLoading || exportPdfLoading} onClick={exportPdf}>
                {exportPdfLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    <span>Export PDF...</span>
                  </>
                ) : (
                  <>
                    <Icon name="file-doc" />
                    <span>Export PDF</span>
                  </>
                )}
              </Button>
              <Button color="primary" onClick={() => loadData(currentPage)}>
                <Icon name="reload" />
                <span>Actualiser</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard>
            <Row className="g-3">
              <Col md="4">
                <label className="form-label">Site</label>
                <RSelect
                  options={siteOptions}
                  value={siteOptions.find((option) => option.value === String(filters.site_id || "")) || siteOptions[0]}
                  onChange={(option) => setFilters((prev) => ({ ...prev, site_id: option?.value || "" }))}
                />
              </Col>
              <Col md="4">
                <label className="form-label">Ecarts non traites depuis (jours)</label>
                <input
                  type="number"
                  className="form-control"
                  min={7}
                  max={365}
                  value={filters.days_unresolved}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      days_unresolved: Math.max(7, Math.min(365, Number(e.target.value || 30))),
                    }))
                  }
                />
              </Col>
              <Col md="4">
                <label className="form-label">Niveau de risque</label>
                <RSelect
                  options={[
                    { value: "all", label: "Tous niveaux" },
                    { value: "high", label: "Risque eleve" },
                    { value: "medium", label: "Risque moyen" },
                    { value: "low", label: "Risque faible" },
                  ]}
                  value={
                    [{ value: "all", label: "Tous niveaux" }, { value: "high", label: "Risque eleve" }, { value: "medium", label: "Risque moyen" }, { value: "low", label: "Risque faible" }].find(
                      (option) => option.value === filters.risk_level
                    ) || { value: "all", label: "Tous niveaux" }
                  }
                  onChange={(option) => setFilters((prev) => ({ ...prev, risk_level: option?.value || "all" }))}
                />
              </Col>
            </Row>
          </PreviewCard>
        </Block>

        <Block>
          <Row className="g-3">
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>Total</h6><div className="amount">{summary.total_gaps || 0}</div></div></PreviewCard>
            </Col>
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>Risque eleve</h6><div className="amount text-danger">{summary.high_risk || 0}</div></div></PreviewCard>
            </Col>
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>Risque moyen</h6><div className="amount text-warning">{summary.medium_risk || 0}</div></div></PreviewCard>
            </Col>
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>Risque faible</h6><div className="amount text-success">{summary.low_risk || 0}</div></div></PreviewCard>
            </Col>
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>En retard</h6><div className="amount text-danger">{summary.overdue || 0}</div></div></PreviewCard>
            </Col>
            <Col sm="6" lg="4">
              <PreviewCard><div className="card-inner py-3"><h6>Site prioritaire</h6><div className="amount text-primary" style={{ fontSize: "1rem" }}>{summary.top_site || "Aucun"}</div></div></PreviewCard>
            </Col>
          </Row>
        </Block>

        <Block>
          <PreviewCard>
            <h6 className="title mb-2">Resume pret audit</h6>
            <p className="text-soft mb-3">{auditSummary.executive_summary || "-"}</p>
            <Row className="g-3">
              <Col md="4">
                <h6 className="title-sm">Constats cles</h6>
                <ul className="list-unstyled">
                  {(auditSummary.key_findings || []).map((item, idx) => (
                    <li key={`kf-${idx}`} className="mb-1 text-soft">- {item}</li>
                  ))}
                </ul>
              </Col>
              <Col md="4">
                <h6 className="title-sm">Actions urgentes</h6>
                <ul className="list-unstyled">
                  {(auditSummary.urgent_actions || []).map((item, idx) => (
                    <li key={`ua-${idx}`} className="mb-1 text-soft">- {item}</li>
                  ))}
                </ul>
              </Col>
              <Col md="4">
                <h6 className="title-sm">Perimetre audit suggere</h6>
                <p className="text-soft mb-0">{auditSummary.suggested_audit_scope || "-"}</p>
              </Col>
            </Row>
          </PreviewCard>
        </Block>

        <Block>
          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead className="nk-tb-item nk-tb-head">
                <DataTableRow><span className="sub-text">Observation</span></DataTableRow>
                <DataTableRow><span className="sub-text">Site</span></DataTableRow>
                <DataTableRow><span className="sub-text">Priorite</span></DataTableRow>
                <DataTableRow><span className="sub-text">Delais</span></DataTableRow>
                <DataTableRow><span className="sub-text">Responsable</span></DataTableRow>
                <DataTableRow><span className="sub-text">Action recommandee</span></DataTableRow>
              </DataTableHead>

              {loading ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={6} className="text-center py-4">
                      <Spinner color="primary" />
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : rows.length === 0 ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={6} className="text-center py-4 text-soft">
                      Aucun ecart critique sur les filtres selectionnes
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : (
                rows.map((item) => {
                  const badge = riskBadge[item.risk_level] || riskBadge.low;
                  return (
                    <DataTableItem key={item.observation_id}>
                      <DataTableRow>
                        <div>
                          <span className="tb-lead">#{item.observation_id} - {item.title}</span>
                          <span className="tb-sub d-block text-soft">Type: {item.type} | Severite: {item.severity}</span>
                        </div>
                      </DataTableRow>
                      <DataTableRow>
                        <span>{item.site?.name || "Site non defini"}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <div>
                          <Badge color={badge.color}>{badge.label}</Badge>
                          <div className="tb-sub mt-1">Score {item.risk_score || 0}</div>
                        </div>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-sub d-block">{item.days_open || 0}j ouverts</span>
                        <span className="tb-sub d-block text-soft">{item.overdue_days || 0}j retard</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-sub d-block">{item.recommended_owner || item.owner || "-"}</span>
                        <span className="tb-sub d-block text-soft">Echeance: {item.recommended_deadline || "-"}</span>
                      </DataTableRow>
                      <DataTableRow>
                        <span className="tb-sub">{item.recommended_action || "-"}</span>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              )}
            </DataTableBody>
            <div className="card-inner">
              {totalItems > 0 && (
                <PaginationComponent
                  itemPerPage={pagination.per_page || perPage}
                  totalItems={totalItems}
                  paginate={(pageNumber) => loadData(pageNumber)}
                  currentPage={pagination.current_page || currentPage}
                />
              )}
            </div>
          </DataTable>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default ComplianceIntelligentAssistantPage;
